import React from 'react';
import { Callout, DirectionalHint, Icon } from '@fluentui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useGetQuicksearchQuery } from '../../store/Api';
import { useGetStorefrontsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import useCan, { SEARCH } from '../../permissions/ability';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { ISearchRequest } from '../../models/Search';
import {
    cascadeRemoveSugarToken,
    criteriaToSugarTokens,
    expandAttributeBoosts,
    extractKeywordText,
    isCompleteSugarToken,
    normalizeForMatch,
    parseProductQuery,
    PSEUDONYM_ATTRIBUTES,
    quoteSugarValue,
    sugarChipLabel,
    tokenizeProductQuery,
} from '../GlobalSearch/productQueryParser';
import SearchFilter from '../Common/SearchFilter';
import { productQueryClasses as classes } from '../GlobalSearch/productQueryStyles';
import {
    taxonomyMatch,
    useAttributePills,
    useFacetIntel,
    useProductCompletion,
    useProductTaxonomy,
    useSearchPopularity,
} from '../GlobalSearch/useProductQueryIntel';

const DEBOUNCE_MS = 250;

interface IProductSearchBoxProps {
    catalogName?: string;
    /**
     * Controlled mode: the criteria the chips hydrate from and that an apply is merged onto.
     *
     * Omitted (the search page), the box reads the persisted `search.criteria` slice, which is what
     * makes it that page's criteria surface. Supplied, redux is not consulted at all, so a box mounted
     * somewhere else (the storefront wizard) neither inherits the search page's saved chips nor
     * carries its own back to it. Give it a value with a stable identity between applies; every
     * change of it re-derives the chips and the typed text.
     */
    criteria?: ISearchRequest;
    /** Live free-text sync: sidebar-triggered searches read the page's keyword state. */
    onKeywordChange: (keywords: string) => void;
    /**
     * Apply fully-merged criteria. The box owns the sugar-expressible fields (keywords,
     * manufacturer, mpn, taxonomy, vendor, price, boost, must) and passes everything else
     * through from the current criteria; the page decides dispatch-vs-refetch.
     */
    onApply: (criteria: ISearchRequest) => void;
    /** A product suggestion was picked — open its details. */
    onProductSelected: (id: string) => void;
    /**
     * Header-dock mode: the box lives inside the fixed 48px header, so it must stay a single
     * line — chips don't wrap, overflow scrolls horizontally inside the field.
     */
    docked?: boolean;
}

/**
 * The search page's query composer: the global palette's product ('>') mode adapted into an
 * inline, always-product-mode input that REPLACES the old react-autosuggest QuickSearch box.
 *
 * Differences from the palette's passthrough mode, by design:
 *  - No sigil. Every keystroke is already a product query.
 *  - Criteria persist. Chips hydrate from the redux criteria (the box is the criteria surface
 *    for the fields sugar can express) and survive across searches and revisits; the palette's
 *    chips are ephemeral.
 *  - The suggestion surface is a flyout that opens on click/focus — the palette's product-mode
 *    experience (frequent pills, as-typed filters, attribute pills and values) plus the
 *    quicksearch rows the old autosuggest had (individual products, tags, product lines).
 *    Hitting Enter runs the search and the flyout goes away; the next click or keystroke
 *    brings it back.
 *  - Chip removal and pill/suggestion picks apply immediately, the way the sidebar filters
 *    do; free text applies on Enter.
 */
const ProductSearchBox: React.FC<IProductSearchBoxProps> = (props) => {
    const canSearch = useCan(SEARCH);
    const persistedCriteria = useSelector((state: RootState) => state.search.criteria);
    // The one place the two modes differ. Everything downstream (chip hydration, the apply merge,
    // the list-criteria picks) reads this, so an embedded mount is byte-identical apart from where
    // its criteria come from.
    const criteria = props.criteria ?? persistedCriteria;

    const [sugarChips, setSugarChips] = React.useState<string[]>([]);
    const [query, setQuery] = React.useState('');
    const [focused, setFocused] = React.useState(false);
    const [selectedAttribute, setSelectedAttribute] = React.useState<string | undefined>();
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const textMirrorRef = React.useRef<HTMLSpanElement>(null);
    const [typedTextWidth, setTypedTextWidth] = React.useState(0);

    // The box is the criteria surface: whenever criteria change (a search ran, a saved search
    // loaded, the sidebar filtered, the palette dispatched), chips and free text re-derive from
    // them. Our own applies land here too, which conveniently normalizes hand-typed sugar.
    React.useEffect(() => {
        const { chips, keywords } = criteriaToSugarTokens(criteria);
        setSugarChips(chips);
        setQuery(keywords);
    }, [criteria]);

    const { onKeywordChange } = props;
    React.useEffect(() => {
        // Only the free text's keyword part counts — a pending "name=" is not a keyword.
        // Verbatim (quotes kept): quoted keywords mean exact-phrase matching downstream.
        onKeywordChange(extractKeywordText(query));
    }, [query, onKeywordChange]);

    const composed = React.useMemo(
        () => '> ' + [...sugarChips, query].filter((s) => s.length > 0).join(' '),
        [sugarChips, query]
    );
    const parsed = React.useMemo(() => parseProductQuery(composed.trim()), [composed]);
    const debouncedComposed = useDebouncedValue(composed, DEBOUNCE_MS);
    const debouncedParsed = React.useMemo(() => parseProductQuery(debouncedComposed.trim()), [debouncedComposed]);

    const { knownManufacturers, validCategories, validSubCategories } = useProductTaxonomy(canSearch);
    const { orgSuggestions, popularityOf } = useSearchPopularity(canSearch);

    const facetPreviewEnabled = canSearch
        && !!(debouncedParsed.criteria.category || debouncedParsed.criteria.subCategory || (debouncedParsed.criteria.manufacturer?.length ?? 0) > 0);
    const { facets, aliasedFacetKeys } = useFacetIntel({
        enabled: facetPreviewEnabled,
        attributeGate: !!(debouncedParsed.criteria.category || debouncedParsed.criteria.subCategory),
        catalogName: props.catalogName,
        criteria: debouncedParsed.criteria,
    });
    const { facetNames, pseudonymPills } = useAttributePills({
        enabled: canSearch,
        category: debouncedParsed.criteria.category,
        facets,
        aliasedFacetKeys,
    });

    const debouncedKeywords = debouncedParsed.keywords;
    const quicksearch = useGetQuicksearchQuery(
        { catalog: props.catalogName, value: debouncedKeywords },
        { skip: !canSearch || debouncedKeywords.length < 2 }
    );

    const completion = useProductCompletion({
        parsed,
        query,
        facets,
        aliasedFacetKeys,
        quicksearchData: quicksearch.data,
        manufacturerItems: knownManufacturers.data?.items,
        validCategories,
        popularityOf,
    });

    const storefronts = useGetStorefrontsQuery(undefined, { skip: parsed.storefrontNames.length === 0 });

    React.useEffect(() => {
        setTypedTextWidth(textMirrorRef.current?.offsetWidth ?? 0);
    }, [query]);

    /**
     * The merge: box-owned fields come from the given chips + text, everything else (tags,
     * range filters, excludes, in-stock/vendor scoping, sort, page size, …) passes through
     * from the current criteria. Absent box fields clear explicitly — removing the mfr chip
     * must actually drop the manufacturer filter.
     */
    const buildCriteria = (chips: string[], text: string): ISearchRequest => {
        const p = parseProductQuery('> ' + [...chips, text].filter((s) => s.length > 0).join(' '));
        const next: ISearchRequest = {
            ...criteria,
            page: 1,
            // Keywords come from the raw text, not the parse — parseProductQuery unquotes
            // them, and "ABC123" (quoted) means an exact-phrase match downstream.
            keywords: extractKeywordText(text),
            manufacturer: p.criteria.manufacturer ?? [],
            manufacturerPartNumber: p.criteria.manufacturerPartNumber,
            segment: p.criteria.segment,
            category: p.criteria.category,
            subCategory: p.criteria.subCategory,
            vendor: p.criteria.vendor ?? [],
            lowPrice: p.criteria.lowPrice,
            highPrice: p.criteria.highPrice,
            // Attribute asks re-expand against fresh facets on every apply (pseudonym fan-out,
            // slot inference, weights) — the chips carry the ask, not the expansion.
            boost: expandAttributeBoosts(p.criteria.boost ?? [], facets),
            must: p.criteria.must ?? [],
        };
        // Sidebar-owned toggles are preserved unless sugar explicitly said otherwise.
        if (p.criteria.inStock !== undefined) next.inStock = p.criteria.inStock;
        if (p.criteria.inStorefront !== undefined) next.inStorefront = p.criteria.inStorefront;
        if (p.storefrontNames.length > 0) {
            const wanted = p.storefrontNames.map((n) => n.toLowerCase());
            const ids = (storefronts.data?.items ?? [])
                .filter((sf) => wanted.some((w) => (sf.name ?? '').toLowerCase() === w || (sf.subUrl ?? '').toLowerCase() === w))
                .map((sf) => sf.id ?? '')
                .filter(Boolean);
            if (ids.length) next.storefrontFilter = ids;
        }
        return next;
    };

    const applyFrom = (chips: string[], text: string) => {
        setSugarChips(chips);
        setQuery(text);
        props.onApply(buildCriteria(chips, text));
    };

    const removeChip = (token: string) => applyFrom(cascadeRemoveSugarToken(sugarChips, token), query);

    const acceptCompletion = () => {
        if (!completion) return;
        const tokens = tokenizeProductQuery(query.trim());
        const remaining = tokens.slice(0, -1);
        if (completion.trailing === '') {
            // A pending `"Name"=` stays in the input so the value can be typed onto it.
            setQuery([...remaining, completion.replacement].join(' '));
        } else {
            setSugarChips((chips) => [...chips, completion.replacement]);
            setQuery(remaining.join(' '));
        }
    };

    // "As typed" quick-filter pills: the bare keywords become one-click filters. Category and
    // subcategory only offer when the typed text matches a value that HAS products.
    const asTypedPills = React.useMemo(() => {
        if (!parsed.keywords) return [];
        const value = parsed.keywordTokens.join(' ');
        const category = parsed.criteria.category ? undefined : taxonomyMatch(validCategories, value);
        const subCategory = parsed.criteria.subCategory ? undefined : taxonomyMatch(validSubCategories, value);
        return [
            { field: 'Manufacturer', token: `mfr:${quoteSugarValue(value)}` },
            { field: 'Part#', token: `mpn:${quoteSugarValue(value)}` },
            ...(category ? [{ field: 'Category', token: `cat:${quoteSugarValue(category)}` }] : []),
            ...(subCategory ? [{ field: 'Subcategory', token: `sub:${quoteSugarValue(subCategory)}` }] : []),
        ];
    }, [parsed, validCategories, validSubCategories]);

    // Frequent searches (org search history), the palette's empty-composer row: only when
    // nothing has been composed yet — chips or typing switch the surface to contextual offers.
    const frequentPills = React.useMemo(() => {
        if (query.trim() || sugarChips.length > 0) return [];
        return [
            ...(orgSuggestions.data?.categories ?? []).slice(0, 4).map((t) => ({
                key: `freq-cat-${t.value}`,
                label: t.value,
                title: `Frequently searched category (${t.count} recent searches)`,
                token: `cat:${quoteSugarValue(t.value)}`,
                italic: false,
            })),
            ...(orgSuggestions.data?.manufacturers ?? []).slice(0, 4).map((t) => ({
                key: `freq-mfr-${t.value}`,
                label: t.value,
                title: `Frequently searched brand (${t.count} recent searches)`,
                token: `mfr:${quoteSugarValue(t.value)}`,
                italic: true,
            })),
        ];
    }, [query, sugarChips, orgSuggestions.data]);

    // A pending typed attribute ("Total Installed System Memory"= — or an alias like ram=)
    // opens its values row, same as a clicked pill.
    const shownAttribute = React.useMemo(() => {
        if (selectedAttribute) return selectedAttribute;
        if (!parsed.pendingAttribute) return undefined;
        const lower = parsed.pendingAttribute.toLowerCase();
        const exact = Object.keys(facets ?? {}).find((k) => k.toLowerCase() === lower);
        return exact ?? aliasedFacetKeys(parsed.pendingAttribute)[0];
    }, [selectedAttribute, parsed.pendingAttribute, facets, aliasedFacetKeys]);

    const shownAttributeValues = React.useMemo(() => {
        if (!shownAttribute) return [];
        const pseudonym = PSEUDONYM_ATTRIBUTES.find((p) => p.label === shownAttribute);
        if (!pseudonym) return facets?.[shownAttribute] ?? [];
        const seen = new Set<string>();
        const union: string[] = [];
        for (const key of aliasedFacetKeys(pseudonym.alias)) {
            for (const v of facets?.[key] ?? []) {
                const norm = normalizeForMatch(v);
                if (!seen.has(norm)) {
                    seen.add(norm);
                    union.push(v);
                }
            }
        }
        return union;
    }, [shownAttribute, facets, aliasedFacetKeys]);

    React.useEffect(() => {
        if (!facets) setSelectedAttribute(undefined);
    }, [facets]);

    const attributeToken = (name: string, value: string) => `${quoteSugarValue(name)}=${quoteSugarValue(value)}`;
    const hasChipToken = (token: string) =>
        parsed.sugarTokens.some((t) => t.toLowerCase() === token.toLowerCase());
    const toggleAttributeValue = (name: string, value: string) => {
        const token = attributeToken(name, value);
        if (hasChipToken(token)) {
            applyFrom(sugarChips.filter((t) => t.toLowerCase() !== token.toLowerCase()), query);
            return;
        }
        // Drop the pending `name=` (or partial `alias=3`) that opened this values row — leaving
        // it would send a half-typed attribute along with the search.
        const text = tokenizeProductQuery(query).filter((t) => {
            const m = t.match(/^("[^"]*"|[^\s="]+)=/);
            if (!m) return true;
            const tokenName = m[1].replace(/^"|"$/g, '');
            return tokenName.toLowerCase() !== name.toLowerCase()
                && tokenName.toLowerCase() !== (selectedAttribute ?? '').toLowerCase()
                && tokenName.toLowerCase() !== (parsed.pendingAttribute ?? '').toLowerCase();
        }).join(' ');
        applyFrom([...sugarChips, token], text);
    };

    const toggleChipToken = (token: string) => {
        if (hasChipToken(token)) applyFrom(cascadeRemoveSugarToken(sugarChips, token), query);
        else applyFrom([...sugarChips, token], '');
    };

    const onInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const next = ev.target.value;
        // Space commits: completed sugar tokens promote out of the free text into chips; the
        // token still being typed stays put. An "=" suspends promotion — the whole text may be
        // one spaced attribute name ("Total Installed System Memory=32 GB").
        if (/\s$/.test(next) && !next.includes('=')) {
            const tokens = tokenizeProductQuery(next);
            const promoted = tokens.filter(isCompleteSugarToken);
            if (promoted.length > 0) {
                setSugarChips((chips) => [...chips, ...promoted]);
                setQuery(tokens.filter((t) => !isCompleteSugarToken(t)).join(' ') + ' ');
                setFocused(true);
                return;
            }
        }
        setQuery(next);
        // A keystroke re-opens suggestions dismissed by Enter (the input still has DOM focus,
        // so no focus event will fire to do it).
        setFocused(true);
    };

    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === 'Tab' && completion) {
            ev.preventDefault();
            acceptCompletion();
            return;
        }
        if (ev.key === 'Enter') {
            ev.preventDefault();
            // Running the search dismisses the suggestion surface - it was offering help
            // composing the query just submitted. A click or keystroke brings it back.
            setFocused(false);
            applyFrom(sugarChips, query);
            return;
        }
        if (ev.key === 'Backspace' && query === '' && sugarChips.length > 0) {
            ev.preventDefault();
            // Backing into a chip deletes it, same as clicking its X (cascade + immediate
            // apply) - unlike the palette, which re-opens the chip for editing.
            removeChip(sugarChips[sugarChips.length - 1]);
            return;
        }
        if (ev.key === 'Escape') {
            setFocused(false);
        }
    };

    // Quicksearch rows: individual products (replacing the old autosuggest's product rows),
    // plus the sections omnibox sugar cannot express. Manufacturers/categories are covered by
    // Tab completion and the as-typed pills instead.
    const productSuggestions = React.useMemo(
        () => Object.entries(quicksearch.data?.productNamesWithIds ?? {}).slice(0, 8),
        [quicksearch.data]
    );
    const applyListCriteria = (mutate: (next: ISearchRequest) => void) => {
        const next = buildCriteria(sugarChips, '');
        mutate(next);
        setQuery('');
        setFocused(false);
        props.onApply(next);
    };
    const suggestionSections: { title: string, rows: { key: string, label: string, chipColor?: string, onPick: () => void }[] }[] = React.useMemo(() => {
        if (debouncedKeywords.length < 2 || !quicksearch.data) return [];
        const data = quicksearch.data;
        return [
            {
                title: 'Products',
                rows: productSuggestions.map(([name, id]) => ({
                    key: `product-${id}`,
                    label: name,
                    onPick: () => {
                        setFocused(false);
                        props.onProductSelected(id);
                    },
                })),
            },
            {
                title: 'Tags',
                rows: (data.tags ?? []).slice(0, 6).map((tag) => ({
                    key: `tag-${tag}`,
                    label: tag,
                    chipColor: '#9BD3AB',
                    onPick: () => applyListCriteria((next) => { next.tags = [...(criteria.tags ?? []), tag]; }),
                })),
            },
            {
                title: 'My Tags',
                rows: (data.userTags ?? []).slice(0, 6).map((tag) => ({
                    key: `usertag-${tag}`,
                    label: tag,
                    chipColor: '#F9D585',
                    onPick: () => applyListCriteria((next) => { next.userTags = [...(criteria.userTags ?? []), tag]; }),
                })),
            },
            {
                title: 'Product Lines',
                rows: (data.productLines ?? []).slice(0, 6).map((line) => ({
                    key: `line-${line}`,
                    label: line,
                    onPick: () => applyListCriteria((next) => { next.productLines = [...(criteria.productLines ?? []), line]; }),
                })),
            },
        ].filter((section) => section.rows.length > 0);
    }, [quicksearch.data, debouncedKeywords, productSuggestions, criteria, sugarChips, facets]);

    const hasAttributeRows = !!facets && (facetNames.length > 0 || pseudonymPills.length > 0);
    const surfaceOpen = focused && (
        frequentPills.length > 0
        || asTypedPills.length > 0
        || hasAttributeRows
        || (!!shownAttribute && shownAttributeValues.length > 0)
        || suggestionSections.length > 0
    );

    return (
        <div style={props.docked ? { width: '100%' } : undefined}>
            <div
                ref={fieldRef}
                className={classes.tokenField}
                style={props.docked ? { flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', margin: 0 } : undefined}
                onClick={() => {
                    inputRef.current?.focus();
                    setFocused(true);
                }}
            >
                <Icon iconName='Search' styles={{ root: { color: '#789c2f', fontSize: 14, flexShrink: 0 } }} />
                {sugarChips.map((token, index) => (
                    <span
                        key={`${token}-${index}`}
                        className={`${classes.pill} ${classes.pillOn}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'default', flexShrink: 0 }}
                    >
                        {sugarChipLabel(token)}
                        <button
                            type='button'
                            aria-label={`Remove ${sugarChipLabel(token)}`}
                            onClick={(ev) => {
                                ev.stopPropagation();
                                removeChip(token);
                            }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}
                        >
                            <Icon iconName='Cancel' styles={{ root: { fontSize: 9 } }} />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    className={classes.tokenInput}
                    aria-label='Product search query'
                    placeholder={sugarChips.length === 0 ? 'Search products (try cat:Laptops ram=16gb, Tab completes)' : ''}
                    value={query}
                    style={completion ? { flexGrow: 0, minWidth: 20, width: Math.max(typedTextWidth + 12, 20) } : undefined}
                    onChange={onInputChange}
                    onKeyDown={onKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                />
                {completion && (
                    <button
                        type='button'
                        className={classes.suggestionPill}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={acceptCompletion}
                    >
                        <span className={classes.suggestionTabChip}>TAB {'⇥'}</span>
                        <span className={classes.suggestionValue}>{completion.value}</span>
                        <span className={classes.suggestionField}>· {completion.field}</span>
                    </button>
                )}
                <span ref={textMirrorRef} className={classes.textMirror} aria-hidden>{query}</span>
            </div>
            {surfaceOpen && (
                <Callout
                    target={fieldRef}
                    isBeakVisible={false}
                    gapSpace={2}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    calloutWidth={fieldRef.current?.offsetWidth}
                    setInitialFocus={false}
                    onDismiss={() => setFocused(false)}
                    styles={{ root: { maxHeight: 480, overflowY: 'auto' } }}
                >
                    {/* mousedown preventDefault keeps the input focused while picking inside
                        the surface — blur would otherwise close it before the click lands. */}
                    <div style={{ padding: '6px 12px 10px' }} onMouseDown={(ev) => ev.preventDefault()}>
                        {frequentPills.length > 0 && (
                            <div className={classes.filterGroupRow}>
                                <span className={classes.filterGroupLabel}>Frequent</span>
                                <div className={classes.filterOptions}>
                                    {frequentPills.map((pill) => (
                                        <button
                                            key={pill.key}
                                            type='button'
                                            className={classes.pill}
                                            style={{ font: 'inherit', fontStyle: pill.italic ? 'italic' : undefined }}
                                            title={pill.title}
                                            onClick={() => toggleChipToken(pill.token)}
                                        >
                                            {pill.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {asTypedPills.length > 0 && (
                            <div className={classes.filterGroupRow}>
                                <span className={classes.filterGroupLabel}>Search by</span>
                                <div className={classes.filterOptions}>
                                    {asTypedPills.map((pill) => (
                                        <button
                                            key={pill.token}
                                            type='button'
                                            className={classes.pill}
                                            style={{ font: 'inherit' }}
                                            onClick={() => applyFrom([...sugarChips, pill.token], '')}
                                        >
                                            <span className={classes.pillField}>{pill.field}:</span> {parsed.keywordTokens.join(' ')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {hasAttributeRows && (
                            <div className={classes.filterGroupRow}>
                                <span className={classes.filterGroupLabel}>Attributes</span>
                                <div className={classes.filterOptions}>
                                    {pseudonymPills.map((p) => (
                                        <button
                                            key={`pseudo-${p.label}`}
                                            type='button'
                                            className={`${classes.pill} ${selectedAttribute === p.label ? classes.pillOn : ''}`}
                                            style={{ font: 'inherit', fontStyle: 'italic' }}
                                            onClick={() => setSelectedAttribute((cur) => (cur === p.label ? undefined : p.label))}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                    {facetNames.map((name) => (
                                        <button
                                            key={`attr-${name}`}
                                            type='button'
                                            className={`${classes.pill} ${selectedAttribute === name ? classes.pillOn : ''}`}
                                            style={{ font: 'inherit' }}
                                            onClick={() => setSelectedAttribute((cur) => (cur === name ? undefined : name))}
                                        >
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {shownAttribute && shownAttributeValues.length > 0 && (
                            <div className={classes.filterGroupRow}>
                                <span className={classes.filterGroupLabel}>{shownAttribute}</span>
                                <div className={classes.filterOptions}>
                                    {shownAttributeValues.slice(0, 20).map((value) => {
                                        const pseudonym = PSEUDONYM_ATTRIBUTES.find((p) => p.label === shownAttribute);
                                        const tokenName = pseudonym ? pseudonym.alias : shownAttribute;
                                        return (
                                            <button
                                                key={`val-${value}`}
                                                type='button'
                                                className={`${classes.pill} ${hasChipToken(attributeToken(tokenName, value)) ? classes.pillOn : ''}`}
                                                style={{ font: 'inherit' }}
                                                onClick={() => toggleAttributeValue(tokenName, value)}
                                            >
                                                {value}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {suggestionSections.map((section) => (
                            <div key={section.title} style={{ margin: '6px -12px 0' }}>
                                <div style={{ padding: '2px 12px', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#605e5c' }}>
                                    {section.title}
                                </div>
                                {section.rows.map((row) => (
                                    <div
                                        key={row.key}
                                        role='button'
                                        tabIndex={-1}
                                        style={{ padding: '6px 12px', cursor: 'pointer' }}
                                        onClick={row.onPick}
                                        onMouseEnter={(ev) => { (ev.currentTarget as HTMLElement).style.backgroundColor = '#f3f2f1'; }}
                                        onMouseLeave={(ev) => { (ev.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                                    >
                                        {row.chipColor
                                            ? <SearchFilter protected={true} color={row.chipColor}>{row.label}</SearchFilter>
                                            : row.label}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </Callout>
            )}
        </div>
    );
};

export default ProductSearchBox;
